import * as THREE from 'three';
import {
  createLavaMaterial,
  createLunarCraterGeometry,
  createLunarSurfacePatchGeometry,
  createMeteorRockGeometry,
  LUNAR_ASSET_COLORS,
  lunarCraterProfile,
  lunarCraterSurfaceShade,
} from '../assets/LunarAssetKit';
import { ImpactBurst } from '../entities/ImpactBurst';
import type { MoonDesignDraft } from '../game/MoonDesign';

export type MeteorSize = 'small' | 'medium' | 'large';
export type SculptingState = 'ready' | 'aiming' | 'flying' | 'reloading';
export type CraterState = 'fresh' | 'erupting' | 'cooling' | 'mare';
export type SculptingUndoKind = 'impact' | 'lava';

export type SculptingSnapshot = {
  active: boolean;
  state: SculptingState;
  meteorSize: MeteorSize;
  craterCount: number;
  mareCount: number;
  selectedCraterId: number | null;
  selectedCraterState: CraterState | null;
  canErupt: boolean;
  lavaAction: 'erupt' | 'stop' | null;
  lavaProgress: number;
  lavaCoolingProgress: number;
  lavaCompositeProgress: number;
  lavaOpacity: number;
  activeLavaCount: number;
  activeProjectileCount: number;
  peakProjectileCount: number;
  canFinalize: boolean;
  activeLavas: Array<{
    id: number;
    state: Extract<CraterState, 'erupting' | 'cooling'>;
    lavaProgress: number;
    coolingProgress: number;
  }>;
  projectileSpeed: number;
  canUndo: boolean;
  undoKind: SculptingUndoKind | null;
  historyDepth: number;
  canRedo: boolean;
  redoKind: SculptingUndoKind | null;
  redoDepth: number;
  craterDepths: number[];
  craterDepthRatios: number[];
  impactMarkerScale: number;
  launcherScreen: { x: number; y: number };
  predictedImpactNormal: { x: number; y: number; z: number } | null;
  lastImpactNormal: { x: number; y: number; z: number } | null;
  physics: {
    engine: 'custom-fixed-step';
    timestep: number;
    bodies: number;
    colliders: number;
    ccd: boolean;
  };
};

type CraterRecord = {
  id: number;
  size: MeteorSize;
  normal: THREE.Vector3;
  radius: number;
  depth: number;
  state: CraterState;
  lavaProgress: number;
  coolingProgress: number;
  fillScale: number;
  group: THREE.Group;
  basin: THREE.Mesh;
  lava: THREE.Mesh;
  mare: THREE.Mesh;
  mareTextureMask: MareTextureMask | null;
  selection: THREE.LineSegments;
};

type FlyingMeteor = {
  group: THREE.Group;
  rock: THREE.Mesh;
  velocity: THREE.Vector3;
  previousPosition: THREE.Vector3;
  size: MeteorSize;
  age: number;
};

type MareTextureMask = {
  offsets: Uint32Array;
  colors: Uint8ClampedArray;
  opacities: Uint8Array;
};

type CraterHistoryState = Pick<
  CraterRecord,
  'id' | 'size' | 'radius' | 'depth' | 'state' | 'lavaProgress' | 'coolingProgress' | 'fillScale'
> & {
  normal: THREE.Vector3;
};

type SculptingHistoryState = {
  kind: SculptingUndoKind;
  craters: CraterHistoryState[];
  craterId: number;
  selectedCraterId: number | null;
  activeLavaIds: number[];
  lastImpactNormal: THREE.Vector3 | null;
};

const MOON_RADIUS = 2.45;
const FIXED_STEP = 1 / 120;
const PROJECTILE_TIME_SCALE = 1.95;
const MAX_CRATERS = 40;
const MAX_HISTORY = 24;
const MAX_PULL = 1.05;
// Keep a tiny dead zone so a simple tap does not fire, while allowing the
// short pulls needed to reach the Moon's lower edge.
const MIN_PULL_TO_LAUNCH = 0.03;
const LAUNCH_POWER = 5.25;
const LAUNCH_DEPTH = 3.2;
const INWARD_LAUNCH_RATIO = 0.03;
const UPWARD_LAUNCH_BOOST_PORTRAIT = 0.63;
const UPWARD_LAUNCH_BOOST_LANDSCAPE = 0.6;
const MOON_GRAVITY = 1.35;
const LAVA_DURATION = 7.2;
const COOLING_DURATION = 2.4;
const MARE_TEXTURE_OPACITY = 0.86;
const LAVA_SPILL_START = 0.44;
const LAVA_RIM_LEVEL = 0.022;
const LAVA_OVERFLOW_LEVEL = 0.044;
const FRONT = new THREE.Vector3(0, 0, 1);
const HOT_LAVA = new THREE.Color(LUNAR_ASSET_COLORS.hotLava);
const COOL_LAVA = new THREE.Color(LUNAR_ASSET_COLORS.coolLava);
const HOT_EMISSIVE = new THREE.Color(LUNAR_ASSET_COLORS.hotLavaEmissive);
const COOL_EMISSIVE = new THREE.Color(LUNAR_ASSET_COLORS.coolLavaEmissive);

const SIZE_TUNING: Record<MeteorSize, { meteor: number; crater: number; depth: number }> = {
  small: { meteor: 0.72, crater: 0.12, depth: 0.055 },
  medium: { meteor: 1, crater: 0.17, depth: 0.08 },
  large: { meteor: 1.3, crater: 0.24, depth: 0.115 },
};
const CRATER_DEPTH_MIN_SCALE = 0.68;
const CRATER_DEPTH_MAX_SCALE = 1.32;

/**
 * Tablet-first creative sandbox scene. Physics deliberately stays custom and
 * deterministic: pooled projectiles, one spherical collider, and a fixed step.
 */
export class MoonSculptingScene {
  readonly group = new THREE.Group();

  private readonly moonRoot = new THREE.Group();
  private readonly moonGeometry = new THREE.SphereGeometry(MOON_RADIUS, 128, 96);
  private readonly moonBasePositions: Float32Array;
  private moonTextureCanvas: HTMLCanvasElement | null = null;
  private moonBasePixels: Uint8ClampedArray | null = null;
  private moonTextureImageData: ImageData | null = null;
  private moonTextureDirections: Float32Array | null = null;
  private readonly moonTexture = this.createMoonTexture();
  private readonly moonMaterial = new THREE.MeshStandardMaterial({
    color: LUNAR_ASSET_COLORS.surface,
    map: this.moonTexture,
    bumpMap: this.moonTexture,
    bumpScale: 0.018,
    roughness: 0.98,
    metalness: 0,
    vertexColors: true,
  });
  private readonly moon = new THREE.Mesh(this.moonGeometry, this.moonMaterial);
  private readonly craterMaterial = new THREE.MeshStandardMaterial({
    color: LUNAR_ASSET_COLORS.crater,
    roughness: 1,
    metalness: 0,
    vertexColors: true,
  });
  private readonly lavaMaterial = createLavaMaterial();
  private readonly mareMaterial = new THREE.MeshStandardMaterial({
    color: LUNAR_ASSET_COLORS.mare,
    roughness: 0.99,
    metalness: 0,
  });
  private readonly selectionMaterial = new THREE.LineBasicMaterial({
    color: '#ffd36b',
    transparent: true,
    opacity: 0.68,
    depthWrite: false,
  });

  private readonly meteor = new THREE.Group();
  private readonly meteorRock: THREE.Mesh;
  private readonly meteorHitArea: THREE.Mesh;
  private readonly meteorGeometry = createMeteorRockGeometry();
  private readonly meteorMaterial = new THREE.MeshStandardMaterial({
    color: LUNAR_ASSET_COLORS.meteor,
    roughness: 0.98,
    metalness: 0.015,
    flatShading: true,
    vertexColors: true,
  });
  private readonly meteorHitMaterial = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0,
    depthWrite: false,
  });
  private readonly trajectoryGeometry = new THREE.BufferGeometry();
  private readonly trajectoryMaterial = new THREE.LineDashedMaterial({
    color: '#ffe3a0',
    transparent: true,
    opacity: 0.72,
    dashSize: 0.12,
    gapSize: 0.09,
  });
  private readonly trajectory: THREE.Line;
  private readonly impactMarker = new THREE.Group();
  private readonly impactMarkerFillMaterial = new THREE.MeshBasicMaterial({
    color: '#ffe46b',
    transparent: true,
    opacity: 0.2,
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  private readonly impactMarkerRingMaterial = new THREE.MeshBasicMaterial({
    color: '#fff2a6',
    transparent: true,
    opacity: 0.98,
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  private readonly impactMarkerLineMaterial = new THREE.LineBasicMaterial({
    color: '#fff8d2',
    transparent: true,
    opacity: 0.95,
    depthTest: false,
    depthWrite: false,
  });
  private readonly sling = new THREE.Group();
  private readonly slingLeftAnchor = new THREE.Vector3(-0.34, 0.43, 0.03);
  private readonly slingRightAnchor = new THREE.Vector3(0.34, 0.43, 0.03);
  private readonly slingBandGeometry = new THREE.CylinderGeometry(0.021, 0.024, 1, 8, 1);
  private readonly slingBandMaterial = new THREE.MeshStandardMaterial({
    color: '#34231b',
    roughness: 0.9,
    depthTest: false,
    depthWrite: false,
  });
  private readonly slingBandLeft = new THREE.Mesh(this.slingBandGeometry, this.slingBandMaterial);
  private readonly slingBandRight = new THREE.Mesh(this.slingBandGeometry, this.slingBandMaterial);
  private readonly slingPouch = new THREE.Mesh(
    new THREE.SphereGeometry(0.18, 16, 8),
    new THREE.MeshStandardMaterial({
      color: '#5a211c',
      roughness: 0.96,
      metalness: 0,
      depthTest: false,
      depthWrite: false,
    }),
  );
  private readonly lavaLight = new THREE.PointLight('#ff6d32', 0, 8, 2);
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointerNdc = new THREE.Vector2();
  private readonly interactionPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -LAUNCH_DEPTH);
  private readonly pointerWorld = new THREE.Vector3();
  private readonly moonWorldCenter = new THREE.Vector3();
  private readonly projectileVelocity = new THREE.Vector3();
  private readonly flyingMeteors: FlyingMeteor[] = [];
  private readonly craterRecords: CraterRecord[] = [];
  private readonly history: SculptingHistoryState[] = [];
  private readonly redoHistory: SculptingHistoryState[] = [];
  private readonly bursts: ImpactBurst[] = [];
  private readonly launcherAnchor = new THREE.Vector3(0, -2.26, LAUNCH_DEPTH);

  private active = false;
  private state: SculptingState = 'ready';
  private meteorSize: MeteorSize = 'medium';
  private selectedCraterId: number | null = null;
  private readonly activeLavaIds = new Set<number>();
  private activePointerId: number | null = null;
  private pointerMode: 'none' | 'aim' | 'moon' = 'none';
  private pointerStartX = 0;
  private pointerStartY = 0;
  private moonStartYaw = 0;
  private moonStartPitch = 0;
  private pointerMoved = false;
  private accumulator = 0;
  private peakProjectileCount = 0;
  private elapsed = 0;
  private craterId = 0;
  private cameraShake = 0;
  private currentAspect = 16 / 9;
  private lastImpactNormal: THREE.Vector3 | null = null;
  private predictedImpactNormal: THREE.Vector3 | null = null;
  private impactMarkerBaseScale = 1;

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly canvas: HTMLCanvasElement,
  ) {
    this.lavaMaterial.transparent = true;
    this.lavaMaterial.opacity = 1;
    const moonPositions = this.moonGeometry.getAttribute('position') as THREE.BufferAttribute;
    this.moonBasePositions = new Float32Array(moonPositions.array as ArrayLike<number>);
    const moonColors = new Float32Array(moonPositions.count * 3);
    moonColors.fill(1);
    this.moonGeometry.setAttribute('color', new THREE.Float32BufferAttribute(moonColors, 3));
    this.moon.receiveShadow = true;
    this.moonRoot.add(this.moon);
    this.group.add(this.moonRoot);

    this.meteorRock = new THREE.Mesh(this.meteorGeometry, this.meteorMaterial);
    this.meteorRock.castShadow = false;
    // Launcher layering is explicit: bands sit over the wooden fork, the
    // leather pouch sits over the bands, and the rock stays cradled on top.
    this.meteorRock.renderOrder = 22;
    this.meteorHitArea = new THREE.Mesh(new THREE.SphereGeometry(0.42, 10, 8), this.meteorHitMaterial);
    this.meteor.add(this.meteorRock, this.meteorHitArea);
    this.group.add(this.meteor);

    this.trajectoryGeometry.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(38 * 3), 3));
    this.trajectory = new THREE.Line(this.trajectoryGeometry, this.trajectoryMaterial);
    this.trajectory.computeLineDistances();
    this.trajectory.visible = false;
    this.group.add(this.trajectory);

    // Unit-radius marker: its final world-space radius is the crater radius.
    const markerFill = new THREE.Mesh(new THREE.CircleGeometry(0.72, 32), this.impactMarkerFillMaterial);
    const markerRing = new THREE.Mesh(new THREE.RingGeometry(0.78, 1, 40), this.impactMarkerRingMaterial);
    const markerCrossGeometry = new THREE.BufferGeometry();
    markerCrossGeometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute([
        -0.98, 0, 0.012, -0.58, 0, 0.012,
        0.58, 0, 0.012, 0.98, 0, 0.012,
        0, -0.98, 0.012, 0, -0.58, 0.012,
        0, 0.58, 0.012, 0, 0.98, 0.012,
      ], 3),
    );
    const markerCross = new THREE.LineSegments(markerCrossGeometry, this.impactMarkerLineMaterial);
    markerFill.renderOrder = 30;
    markerRing.renderOrder = 31;
    markerCross.renderOrder = 32;
    this.impactMarker.add(markerFill, markerRing, markerCross);
    this.impactMarker.visible = false;
    this.group.add(this.impactMarker);

    this.createSling();
    this.group.add(this.sling, this.lavaLight);
    this.group.visible = false;
    this.applyLayout(this.currentAspect);
    this.resetMeteor();
  }

  setActive(active: boolean): void {
    this.active = active;
    this.group.visible = active;
    if (!active) {
      this.cancelPointer();
      this.clearFlyingMeteors();
    }
    if (active) this.resetMeteor();
  }

  isActive(): boolean {
    return this.active;
  }

  update(delta: number, reducedMotion: boolean): void {
    if (!this.active) return;
    this.elapsed += delta;
    this.applyLayout(this.camera.aspect);

    if (this.flyingMeteors.length > 0) {
      // Advance the same fixed-step trajectory faster in real time. This keeps
      // the aim preview and continuous segment collision exactly in sync.
      this.accumulator += Math.min(delta, 0.08) * PROJECTILE_TIME_SCALE;
      while (this.accumulator >= FIXED_STEP && this.flyingMeteors.length > 0) {
        this.stepProjectiles(FIXED_STEP);
        this.accumulator -= FIXED_STEP;
      }
      for (const projectile of this.flyingMeteors) {
        projectile.rock.rotation.x += delta * 7.2;
        projectile.rock.rotation.y += delta * 3.8;
        projectile.rock.rotation.z += delta * 5.3;
      }
    }

    this.updateLava(delta);
    this.updateBursts(delta, reducedMotion);
    if (this.impactMarker.visible) {
      const pulse = reducedMotion ? 0.5 : (Math.sin(this.elapsed * 7.5) + 1) * 0.5;
      this.impactMarker.scale.setScalar(this.impactMarkerBaseScale);
      this.impactMarkerFillMaterial.opacity = 0.12 + pulse * 0.1;
      this.impactMarkerRingMaterial.opacity = 0.78 + pulse * 0.2;
      this.impactMarkerLineMaterial.opacity = 0.72 + pulse * 0.23;
    }
    this.cameraShake = Math.max(0, this.cameraShake - delta * 2.8);
    const selectedActiveLava = this.selectedCraterId === null || !this.activeLavaIds.has(this.selectedCraterId)
      ? null
      : this.craterRecords.find((crater) => crater.id === this.selectedCraterId) ?? null;
    const activeLava = selectedActiveLava
      ?? this.craterRecords.find((crater) => this.activeLavaIds.has(crater.id))
      ?? null;
    const lavaVisibility = activeLava?.state === 'cooling'
      ? 1 - this.getLavaCompositeProgress(activeLava)
      : activeLava
        ? 1
        : 0;
    this.lavaLight.intensity = lavaVisibility * (1.15 + Math.sin(this.elapsed * 8) * 0.16);
  }

  applyCamera(delta: number, reducedMotion: boolean): void {
    const portrait = this.camera.aspect < 0.9;
    const target = new THREE.Vector3(0, portrait ? -0.22 : 0, portrait ? 14.3 : 11.7);
    this.camera.position.lerp(target, 1 - Math.exp(-delta * 5.2));
    const shake = reducedMotion ? 0 : this.cameraShake * this.cameraShake * 0.075;
    this.camera.position.x += Math.sin(this.elapsed * 79) * shake;
    this.camera.position.y += Math.cos(this.elapsed * 67) * shake;
    this.camera.lookAt(0, portrait ? -0.18 : 0, 0);
  }

  handlePointerDown(event: PointerEvent): boolean {
    if (!this.active || this.activePointerId !== null) return false;
    this.updateRay(event);

    if (this.state === 'ready' && this.raycaster.intersectObject(this.meteorHitArea, true).length > 0) {
      this.activePointerId = event.pointerId;
      this.pointerMode = 'aim';
      this.state = 'aiming';
      this.canvas.setPointerCapture?.(event.pointerId);
      this.dragMeteor(event);
      return true;
    }

    const moonHit = this.raycaster.intersectObject(this.moon, false)[0];
    if (moonHit) {
      this.activePointerId = event.pointerId;
      this.pointerMode = 'moon';
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.moonStartYaw = this.moonRoot.rotation.y;
      this.moonStartPitch = this.moonRoot.rotation.x;
      this.pointerMoved = false;
      this.canvas.setPointerCapture?.(event.pointerId);
      return true;
    }
    return false;
  }

  handlePointerMove(event: PointerEvent): boolean {
    if (!this.active || event.pointerId !== this.activePointerId) return false;
    if (this.pointerMode === 'aim') {
      this.dragMeteor(event);
      return true;
    }
    if (this.pointerMode === 'moon') {
      const dx = event.clientX - this.pointerStartX;
      const dy = event.clientY - this.pointerStartY;
      this.pointerMoved ||= Math.hypot(dx, dy) > 7;
      this.moonRoot.rotation.y = this.moonStartYaw + dx * 0.007;
      this.moonRoot.rotation.x = THREE.MathUtils.clamp(this.moonStartPitch + dy * 0.005, -0.75, 0.75);
      return true;
    }
    return false;
  }

  handlePointerUp(event: PointerEvent): boolean {
    if (!this.active || event.pointerId !== this.activePointerId) return false;
    const mode = this.pointerMode;
    if (mode === 'aim') this.launchMeteor();
    if (mode === 'moon' && !this.pointerMoved) this.selectCraterAt(event);
    this.cancelPointer();
    return mode !== 'none';
  }

  setMeteorSize(size: MeteorSize): void {
    if (this.state === 'aiming') return;
    this.meteorSize = size;
    this.resetMeteor();
  }

  eruptSelected(): boolean {
    if (this.selectedCraterId === null) return false;
    const crater = this.craterRecords.find((candidate) => candidate.id === this.selectedCraterId);
    if (!crater) return false;
    if (crater.state === 'erupting' && this.activeLavaIds.has(crater.id)) {
      this.beginCooling(crater);
      return true;
    }
    if (crater.state !== 'fresh') return false;
    this.pushHistory('lava');
    crater.state = 'erupting';
    crater.lavaProgress = 0;
    crater.coolingProgress = 0;
    crater.fillScale = 0.04;
    crater.lava.visible = true;
    crater.lava.scale.setScalar(crater.fillScale);
    crater.mare.visible = false;
    this.activeLavaIds.add(crater.id);
    this.updateLavaLightPosition(crater);
    return true;
  }

  reset(): void {
    this.clearFlyingMeteors();
    this.accumulator = 0;
    this.peakProjectileCount = 0;
    this.clearCraterRecords();
    this.history.length = 0;
    this.redoHistory.length = 0;
    this.rebuildMoonSurface();
    this.craterId = 0;
    this.selectedCraterId = null;
    this.activeLavaIds.clear();
    this.lastImpactNormal = null;
    this.moonRoot.rotation.set(0.08, -0.16, 0);
    this.clearBursts();
    this.resetMeteor();
  }

  undo(): SculptingUndoKind | null {
    if (this.state === 'aiming' || this.flyingMeteors.length > 0) return null;
    const snapshot = this.history.pop();
    if (!snapshot) return null;
    this.redoHistory.push(this.captureHistoryState(snapshot.kind));
    if (this.redoHistory.length > MAX_HISTORY) this.redoHistory.shift();
    this.restoreHistoryState(snapshot);
    return snapshot.kind;
  }

  redo(): SculptingUndoKind | null {
    if (this.state === 'aiming' || this.flyingMeteors.length > 0) return null;
    const snapshot = this.redoHistory.pop();
    if (!snapshot) return null;
    this.history.push(this.captureHistoryState(snapshot.kind));
    if (this.history.length > MAX_HISTORY) this.history.shift();
    this.restoreHistoryState(snapshot);
    return snapshot.kind;
  }

  private restoreHistoryState(snapshot: SculptingHistoryState): void {
    this.cancelPointer();
    this.clearBursts();
    this.clearCraterRecords();
    this.craterId = 0;

    for (const saved of snapshot.craters) {
      const crater = this.createCrater(saved.normal, saved.size, saved.id, false, saved.depth);
      crater.state = saved.state;
      crater.lavaProgress = saved.lavaProgress;
      crater.coolingProgress = saved.coolingProgress;
      crater.fillScale = saved.fillScale;
      this.applyCraterVisualState(crater);
    }

    this.craterId = snapshot.craterId;
    this.selectedCraterId = snapshot.selectedCraterId;
    this.activeLavaIds.clear();
    for (const id of snapshot.activeLavaIds) this.activeLavaIds.add(id);
    this.lastImpactNormal = snapshot.lastImpactNormal?.clone() ?? null;
    this.rebuildMoonSurface();
    this.selectCrater(this.selectedCraterId);

    const activeLava = this.craterRecords.find((crater) => this.activeLavaIds.has(crater.id));
    if (activeLava) this.updateLavaLightPosition(activeLava);

    this.resetMeteor();
  }

  createDemoCrater(withLava = false): void {
    if (!this.active) this.setActive(true);
    const crater = this.createCrater(new THREE.Vector3(-0.22, 0.2, 0.96).normalize(), 'large');
    this.selectCrater(crater.id);
    if (withLava) this.eruptSelected();
  }

  createDemoDepthVariety(): void {
    if (!this.active) this.setActive(true);
    this.reset();
    const normals = [
      new THREE.Vector3(-0.38, 0.25, 0.89),
      new THREE.Vector3(-0.04, 0.32, 0.95),
      new THREE.Vector3(0.34, 0.22, 0.91),
      new THREE.Vector3(-0.31, -0.13, 0.94),
      new THREE.Vector3(0.03, -0.2, 0.98),
      new THREE.Vector3(0.37, -0.16, 0.92),
    ];
    for (const normal of normals) this.createCrater(normal.normalize(), 'medium');
    this.selectCrater(null);
  }

  createStressCraters(count = MAX_CRATERS): void {
    this.reset();
    const total = Math.min(count, MAX_CRATERS);
    for (let index = 0; index < total; index += 1) {
      const angle = index * 2.399963;
      const radial = 0.18 + 0.72 * Math.sqrt((index + 0.5) / total);
      const normal = new THREE.Vector3(
        Math.cos(angle) * radial,
        Math.sin(angle) * radial,
        Math.sqrt(Math.max(1 - radial * radial, 0.08)),
      ).normalize();
      const size: MeteorSize = index % 9 === 0 ? 'large' : index % 3 === 0 ? 'medium' : 'small';
      const crater = this.createCrater(normal, size);
      crater.state = 'mare';
      crater.lavaProgress = 1;
      crater.fillScale = 1;
      crater.basin.visible = false;
      crater.mare.visible = false;
      crater.mare.scale.setScalar(1);
      crater.mare.position.z = LAVA_OVERFLOW_LEVEL;
    }
    this.rebuildMoonSurface();
    this.selectCrater(this.craterRecords.at(-1)?.id ?? null);
  }

  createDemoMareImpact(): void {
    if (!this.active) this.setActive(true);
    this.reset();
    const impactNormal = new THREE.Vector3(-0.22, 0.2, 0.96).normalize();
    const sea = this.createCrater(impactNormal, 'large');
    sea.state = 'mare';
    sea.lavaProgress = 1;
    sea.fillScale = 1;
    sea.basin.visible = false;
    sea.mare.visible = false;
    this.rebuildMoonSurface();
    const newCrater = this.createCrater(impactNormal, 'medium');
    this.selectCrater(newCrater.id);
  }

  createDemoMarePatches(): void {
    if (!this.active) this.setActive(true);
    this.reset();
    const samples: Array<{ normal: THREE.Vector3; size: MeteorSize; fillScale: number }> = [
      { normal: new THREE.Vector3(-0.42, -0.12, 0.9).normalize(), size: 'large', fillScale: 0.24 },
      { normal: new THREE.Vector3(0.02, -0.02, 1).normalize(), size: 'large', fillScale: 0.43 },
      { normal: new THREE.Vector3(0.36, 0.11, 0.93).normalize(), size: 'medium', fillScale: 0.68 },
    ];
    for (const sample of samples) {
      const sea = this.createCrater(sample.normal, sample.size);
      sea.state = 'mare';
      sea.lavaProgress = sample.fillScale;
      sea.fillScale = sample.fillScale;
      sea.basin.visible = false;
      sea.mare.visible = false;
    }
    this.rebuildMoonSurface();
    this.selectCrater(null);
  }

  createDemoMareTransition(coolingProgress = 0.5): void {
    if (!this.active) this.setActive(true);
    this.reset();
    const crater = this.createCrater(new THREE.Vector3(-0.1, 0.12, 0.99).normalize(), 'large');
    this.selectCrater(crater.id);
    this.eruptSelected();
    crater.lavaProgress = 0.64;
    this.applyCraterVisualState(crater);
    this.beginCooling(crater);
    crater.coolingProgress = THREE.MathUtils.clamp(coolingProgress, 0, 1);
    this.applyCraterVisualState(crater);
    this.rebuildMoonSurface();
  }

  createDemoConcurrentLava(): void {
    if (!this.active) this.setActive(true);
    this.reset();
    const normals = [
      new THREE.Vector3(-0.28, 0.15, 0.95).normalize(),
      new THREE.Vector3(0.28, 0.1, 0.96).normalize(),
    ];
    const craters = normals.map((normal) => this.createCrater(normal, 'large'));
    for (const [index, crater] of craters.entries()) {
      this.selectCrater(crater.id);
      this.eruptSelected();
      crater.lavaProgress = 0.24 + index * 0.12;
      this.applyCraterVisualState(crater);
    }
    this.selectCrater(craters[0].id);
  }

  getSnapshot(): SculptingSnapshot {
    const selectedCrater = this.selectedCraterId === null
      ? null
      : this.craterRecords.find((crater) => crater.id === this.selectedCraterId) ?? null;
    const activeLava = this.craterRecords.find((crater) => this.activeLavaIds.has(crater.id)) ?? null;
    const launcherScreen = this.launcherAnchor.clone().project(this.camera);
    return {
      active: this.active,
      state: this.state,
      meteorSize: this.meteorSize,
      craterCount: this.craterRecords.length,
      mareCount: this.craterRecords.filter((crater) => crater.state === 'mare').length,
      selectedCraterId: this.selectedCraterId,
      selectedCraterState: selectedCrater?.state ?? null,
      canErupt: selectedCrater?.state === 'fresh',
      lavaAction: selectedCrater?.state === 'erupting' && this.activeLavaIds.has(selectedCrater.id)
        ? 'stop'
        : selectedCrater?.state === 'fresh'
          ? 'erupt'
          : null,
      lavaProgress: selectedCrater?.lavaProgress ?? activeLava?.lavaProgress ?? 0,
      lavaCoolingProgress: selectedCrater?.coolingProgress ?? activeLava?.coolingProgress ?? 0,
      lavaCompositeProgress: selectedCrater
        ? this.getLavaCompositeProgress(selectedCrater)
        : activeLava
          ? this.getLavaCompositeProgress(activeLava)
          : 0,
      lavaOpacity: selectedCrater?.lava.visible
        ? (selectedCrater.lava.material as THREE.MeshStandardMaterial).opacity
        : activeLava?.lava.visible
          ? (activeLava.lava.material as THREE.MeshStandardMaterial).opacity
          : 0,
      activeLavaCount: this.activeLavaIds.size,
      activeProjectileCount: this.flyingMeteors.length,
      peakProjectileCount: this.peakProjectileCount,
      canFinalize: this.canFinalizeMoon(),
      activeLavas: this.craterRecords
        .filter((crater) => this.activeLavaIds.has(crater.id) && (crater.state === 'erupting' || crater.state === 'cooling'))
        .map((crater) => ({
          id: crater.id,
          state: crater.state as Extract<CraterState, 'erupting' | 'cooling'>,
          lavaProgress: crater.lavaProgress,
          coolingProgress: crater.coolingProgress,
        })),
      projectileSpeed: this.flyingMeteors.reduce(
        (speed, projectile) => Math.max(speed, projectile.velocity.length()),
        this.projectileVelocity.length(),
      ),
      canUndo: this.history.length > 0 && this.state !== 'aiming' && this.flyingMeteors.length === 0,
      undoKind: this.history.at(-1)?.kind ?? null,
      historyDepth: this.history.length,
      canRedo: this.redoHistory.length > 0 && this.state !== 'aiming' && this.flyingMeteors.length === 0,
      redoKind: this.redoHistory.at(-1)?.kind ?? null,
      redoDepth: this.redoHistory.length,
      craterDepths: this.craterRecords.map((crater) => crater.depth),
      craterDepthRatios: this.craterRecords.map((crater) => crater.depth / SIZE_TUNING[crater.size].depth),
      impactMarkerScale: this.impactMarkerBaseScale,
      launcherScreen: {
        x: launcherScreen.x * 0.5 + 0.5,
        y: -launcherScreen.y * 0.5 + 0.5,
      },
      predictedImpactNormal: this.predictedImpactNormal
        ? {
            x: this.predictedImpactNormal.x,
            y: this.predictedImpactNormal.y,
            z: this.predictedImpactNormal.z,
          }
        : null,
      lastImpactNormal: this.lastImpactNormal
        ? { x: this.lastImpactNormal.x, y: this.lastImpactNormal.y, z: this.lastImpactNormal.z }
        : null,
      physics: {
        engine: 'custom-fixed-step',
        timestep: FIXED_STEP,
        bodies: this.flyingMeteors.length,
        colliders: 1,
        ccd: true,
      },
    };
  }

  canFinalizeMoon(): boolean {
    return this.active
      && this.state === 'ready'
      && this.flyingMeteors.length === 0
      && this.craterRecords.length > 0
      && this.activeLavaIds.size === 0
      && this.craterRecords.every((crater) => crater.state === 'fresh' || crater.state === 'mare');
  }

  exportMoonDesign(): MoonDesignDraft | null {
    if (!this.canFinalizeMoon()) return null;
    let surfaceSeed = 2166136261;
    const craters = this.craterRecords.map((crater) => {
      surfaceSeed = Math.imul(surfaceSeed ^ crater.id, 16777619);
      surfaceSeed = Math.imul(surfaceSeed ^ Math.round((crater.normal.x + 1) * 10_000), 16777619);
      surfaceSeed = Math.imul(surfaceSeed ^ Math.round((crater.normal.y + 1) * 10_000), 16777619);
      return {
        id: crater.id,
        size: crater.size,
        normal: { x: crater.normal.x, y: crater.normal.y, z: crater.normal.z },
        radius: crater.radius,
        depth: crater.depth,
        hasMare: crater.state === 'mare',
        mareFillScale: crater.state === 'mare' ? crater.fillScale : 0,
      };
    });
    return {
      surfaceSeed: surfaceSeed >>> 0,
      craterCount: craters.length,
      mareCount: craters.filter((crater) => crater.hasMare).length,
      craters,
    };
  }

  dispose(): void {
    this.reset();
    this.moonGeometry.dispose();
    this.moonTexture.dispose();
    this.moonMaterial.dispose();
    this.craterMaterial.dispose();
    this.lavaMaterial.dispose();
    this.mareMaterial.dispose();
    this.selectionMaterial.dispose();
    this.meteorGeometry.dispose();
    this.meteorMaterial.dispose();
    this.meteorHitArea.geometry.dispose();
    this.meteorHitMaterial.dispose();
    this.trajectoryGeometry.dispose();
    this.trajectoryMaterial.dispose();
    this.impactMarker.traverse((child) => {
      if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) child.geometry.dispose();
    });
    this.impactMarkerFillMaterial.dispose();
    this.impactMarkerRingMaterial.dispose();
    this.impactMarkerLineMaterial.dispose();
    this.sling.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      child.geometry.dispose();
      (child.material as THREE.Material).dispose();
    });
  }

  private createMoonTexture(): THREE.CanvasTexture {
    const width = 512;
    const height = 256;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not create sculpting moon texture.');
    const image = context.createImageData(width, height);
    const directions = new Float32Array(width * height * 3);
    const hash = (x: number, y: number): number => {
      let value = Math.imul(x, 374761393) + Math.imul(y, 668265263) + 0x5bf03635;
      value = Math.imul(value ^ (value >>> 13), 1274126177);
      return ((value ^ (value >>> 16)) >>> 0) / 4294967295;
    };
    const valueNoise = (u: number, v: number, columns: number, rows: number): number => {
      const sampleX = u * columns;
      const sampleY = v * rows;
      const x0 = Math.floor(sampleX);
      const y0 = Math.floor(sampleY);
      const x1 = (x0 + 1) % columns;
      const y1 = Math.min(y0 + 1, rows);
      const tx = THREE.MathUtils.smoothstep(sampleX - x0, 0, 1);
      const ty = THREE.MathUtils.smoothstep(sampleY - y0, 0, 1);
      const top = THREE.MathUtils.lerp(hash(x0 % columns, y0), hash(x1, y0), tx);
      const bottom = THREE.MathUtils.lerp(hash(x0 % columns, y1), hash(x1, y1), tx);
      return THREE.MathUtils.lerp(top, bottom, ty);
    };
    const textureCraters = Array.from({ length: 64 }, (_, index) => ({
      x: hash(index + 17, 41) * width,
      y: (0.07 + hash(index + 31, 73) * 0.86) * height,
      radius: 1.15 + Math.pow(hash(index + 59, 107), 2.15) * 5.6,
    }));
    for (let y = 0; y < height; y += 1) {
      const phi = (y / (height - 1)) * Math.PI;
      const sinPhi = Math.sin(phi);
      const ny = Math.cos(phi);
      for (let x = 0; x < width; x += 1) {
        const theta = (x / width) * Math.PI * 2;
        const pixel = y * width + x;
        const directionOffset = pixel * 3;
        directions[directionOffset] = -Math.cos(theta) * sinPhi;
        directions[directionOffset + 1] = ny;
        directions[directionOffset + 2] = Math.sin(theta) * sinPhi;
        const u = x / width;
        const v = y / (height - 1);
        const broad = (valueNoise(u, v, 5, 3) - 0.5) * 36;
        const regional = (valueNoise(u, v, 12, 7) - 0.5) * 22;
        const granular = (valueNoise(u, v, 34, 18) - 0.5) * 12;
        const dust = (valueNoise(u, v, 96, 48) - 0.5) * 5;
        let microRelief = 0;
        for (const crater of textureCraters) {
          const directX = Math.abs(x - crater.x);
          const dx = Math.min(directX, width - directX);
          const dy = y - crater.y;
          const distance = Math.hypot(dx, dy);
          if (distance >= crater.radius) continue;
          const t = distance / crater.radius;
          const bowl = -9 * (1 - THREE.MathUtils.smoothstep(t, 0.08, 0.72));
          const rim = 7 * Math.exp(-Math.pow((t - 0.82) / 0.11, 2));
          microRelief += bowl + rim;
        }
        const value = THREE.MathUtils.clamp(Math.round(184 + broad + regional + granular + dust + microRelief), 150, 211);
        const offset = (y * width + x) * 4;
        image.data[offset] = value + 2;
        image.data[offset + 1] = value + 1;
        image.data[offset + 2] = value - 2;
        image.data[offset + 3] = 255;
      }
    }
    context.putImageData(image, 0, 0);
    this.moonTextureCanvas = canvas;
    this.moonBasePixels = new Uint8ClampedArray(image.data);
    this.moonTextureImageData = image;
    this.moonTextureDirections = directions;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    return texture;
  }

  private createSling(): void {
    const wood = new THREE.MeshStandardMaterial({ color: '#8a512c', roughness: 0.78, metalness: 0 });
    const woodShade = new THREE.MeshStandardMaterial({ color: '#5b321d', roughness: 0.88, metalness: 0 });
    const leather = new THREE.MeshStandardMaterial({ color: '#302019', roughness: 0.98, metalness: 0 });
    const tube = (name: string, points: THREE.Vector3[], radius: number, material: THREE.Material): THREE.Mesh => {
      const curve = new THREE.CatmullRomCurve3(points);
      const mesh = new THREE.Mesh(new THREE.TubeGeometry(curve, 16, radius, 10, false), material);
      mesh.name = name;
      return mesh;
    };

    const handle = tube('slingHandle', [
      new THREE.Vector3(0.025, -0.82, 0),
      new THREE.Vector3(-0.015, -0.58, 0.005),
      new THREE.Vector3(0.018, -0.3, -0.004),
      new THREE.Vector3(0, 0.02, 0),
    ], 0.115, wood);
    const leftArm = tube('slingLeftArm', [
      new THREE.Vector3(-0.01, -0.04, 0),
      new THREE.Vector3(-0.12, 0.1, 0.003),
      new THREE.Vector3(-0.23, 0.27, 0.012),
      this.slingLeftAnchor,
    ], 0.095, wood);
    const rightArm = tube('slingRightArm', [
      new THREE.Vector3(0.01, -0.04, 0),
      new THREE.Vector3(0.1, 0.11, -0.006),
      new THREE.Vector3(0.25, 0.25, 0.008),
      this.slingRightAnchor,
    ], 0.098, wood);
    const grain = tube('slingWoodGrain', [
      new THREE.Vector3(0.058, -0.72, 0.096),
      new THREE.Vector3(0.025, -0.49, 0.105),
      new THREE.Vector3(0.05, -0.24, 0.095),
    ], 0.009, woodShade);
    const leftTipWrap = tube('slingLeftTipWrap', [
      new THREE.Vector3(-0.27, 0.32, 0.014),
      this.slingLeftAnchor,
    ], 0.112, leather);
    const rightTipWrap = tube('slingRightTipWrap', [
      new THREE.Vector3(0.28, 0.31, 0.012),
      this.slingRightAnchor,
    ], 0.115, leather);

    this.slingPouch.name = 'slingLeatherPouch';
    this.slingPouch.scale.set(0.92, 0.42, 0.32);
    this.slingPouch.renderOrder = 21;
    this.slingBandLeft.name = 'slingLeftBand';
    this.slingBandRight.name = 'slingRightBand';
    this.slingBandLeft.renderOrder = 20;
    this.slingBandRight.renderOrder = 20;
    this.sling.add(handle, leftArm, rightArm, grain, leftTipWrap, rightTipWrap);
    for (let index = 0; index < 5; index += 1) {
      const wrap = new THREE.Mesh(new THREE.TorusGeometry(0.119, 0.014, 6, 18), leather);
      wrap.name = `slingGripWrap${index}`;
      wrap.rotation.x = Math.PI / 2;
      wrap.position.set(0.006 - index * 0.004, -0.61 + index * 0.062, 0);
      this.sling.add(wrap);
    }
    this.sling.add(this.slingBandLeft, this.slingBandRight, this.slingPouch);
  }

  private applyLayout(aspect: number): void {
    if (Math.abs(aspect - this.currentAspect) < 0.01 && this.group.visible) return;
    this.currentAspect = aspect;
    const portrait = aspect < 0.9;
    this.moonRoot.position.set(0, portrait ? 0.72 : 0.25, 0);
    // Perspective compensation keeps the foreground launcher at the same
    // bottom-center screen position even though it now sits in front of the moon.
    this.launcherAnchor.set(0, portrait ? -1.55 : -1.7, LAUNCH_DEPTH);
    this.sling.position.copy(this.launcherAnchor).add(new THREE.Vector3(0, -0.42, -0.05));
    if (this.state === 'ready' || this.state === 'reloading') this.meteor.position.copy(this.launcherAnchor);
    this.updateSlingBand();
  }

  private resetMeteor(): void {
    this.state = 'ready';
    this.projectileVelocity.set(0, 0, 0);
    this.meteor.visible = true;
    this.meteor.position.copy(this.launcherAnchor);
    const scale = SIZE_TUNING[this.meteorSize].meteor;
    this.meteor.scale.setScalar(scale);
    this.meteor.quaternion.identity();
    this.meteorRock.rotation.set(0.18, -0.32, 0.12);
    this.trajectory.visible = false;
    this.impactMarker.visible = false;
    this.predictedImpactNormal = null;
    this.impactMarkerBaseScale = SIZE_TUNING[this.meteorSize].crater;
    this.updateSlingBand();
  }

  private dragMeteor(event: PointerEvent): void {
    if (!this.pointerToPlane(event, this.pointerWorld)) return;
    const offset = this.pointerWorld.sub(this.launcherAnchor);
    if (offset.length() > MAX_PULL) offset.setLength(MAX_PULL);
    offset.x = THREE.MathUtils.clamp(offset.x, -1.05, 1.05);
    this.meteor.position.copy(this.launcherAnchor).add(offset);
    this.projectileVelocity.copy(this.launcherAnchor).sub(this.meteor.position).multiplyScalar(LAUNCH_POWER);
    if (this.projectileVelocity.y > 0) {
      const upwardBoost = this.currentAspect < 0.9
        ? UPWARD_LAUNCH_BOOST_PORTRAIT
        : UPWARD_LAUNCH_BOOST_LANDSCAPE;
      this.projectileVelocity.y *= upwardBoost;
    }
    const pullStrength = Math.hypot(offset.x, offset.y);
    this.projectileVelocity.z = -pullStrength * LAUNCH_POWER * INWARD_LAUNCH_RATIO;
    this.updateTrajectory();
    this.updateSlingBand();
  }

  private launchMeteor(): void {
    if (this.meteor.position.distanceTo(this.launcherAnchor) < MIN_PULL_TO_LAUNCH) {
      this.resetMeteor();
      return;
    }

    const projectileGroup = new THREE.Group();
    const projectileRock = new THREE.Mesh(this.meteorGeometry, this.meteorMaterial);
    projectileRock.renderOrder = 22;
    projectileRock.rotation.copy(this.meteorRock.rotation);
    projectileGroup.add(projectileRock);
    projectileGroup.position.copy(this.meteor.position);
    projectileGroup.quaternion.copy(this.meteor.quaternion);
    projectileGroup.scale.copy(this.meteor.scale);
    this.group.add(projectileGroup);
    this.flyingMeteors.push({
      group: projectileGroup,
      rock: projectileRock,
      velocity: this.projectileVelocity.clone(),
      previousPosition: this.meteor.position.clone(),
      size: this.meteorSize,
      age: 0,
    });
    this.peakProjectileCount = Math.max(this.peakProjectileCount, this.flyingMeteors.length);
    this.trajectory.visible = false;
    this.impactMarker.visible = false;
    this.predictedImpactNormal = null;
    this.resetMeteor();
  }

  private stepProjectiles(delta: number): void {
    this.getMoonWorldCenter(this.moonWorldCenter);
    for (let index = this.flyingMeteors.length - 1; index >= 0; index -= 1) {
      const projectile = this.flyingMeteors[index];
      projectile.age += delta;
      projectile.previousPosition.copy(projectile.group.position);
      const gravity = this.moonWorldCenter.clone().sub(projectile.group.position);
      const distanceSq = Math.max(gravity.lengthSq(), 2.5);
      gravity.normalize().multiplyScalar(MOON_GRAVITY * (9 / distanceSq));
      projectile.velocity.addScaledVector(gravity, delta);
      projectile.group.position.addScaledVector(projectile.velocity, delta);

      const meteorRadius = 0.19 * SIZE_TUNING[projectile.size].meteor;
      const hit = this.segmentSphereHit(
        projectile.previousPosition,
        projectile.group.position,
        this.moonWorldCenter,
        MOON_RADIUS + meteorRadius,
      );
      if (hit) {
        this.impact(hit, projectile.size);
        this.removeFlyingMeteor(index);
        continue;
      }
      if (projectile.age > 5 || projectile.group.position.length() > 15) this.removeFlyingMeteor(index);
    }
  }

  private impact(worldPoint: THREE.Vector3, size: MeteorSize): void {
    const worldNormal = worldPoint.clone().sub(this.moonWorldCenter).normalize();
    const localNormal = worldNormal.clone().applyQuaternion(this.moonRoot.quaternion.clone().invert());
    this.pushHistory('impact');
    this.lastImpactNormal = localNormal.clone();
    const crater = this.createCrater(localNormal, size);
    this.selectCrater(crater.id);
    const burst = new ImpactBurst(worldPoint.clone().addScaledVector(worldNormal, 0.025), worldNormal, crater.id);
    this.bursts.push(burst);
    this.group.add(burst.group);
    this.cameraShake = Math.max(this.cameraShake, size === 'large' ? 1 : size === 'medium' ? 0.72 : 0.48);
  }

  private removeFlyingMeteor(index: number): void {
    const projectile = this.flyingMeteors[index];
    if (!projectile) return;
    this.group.remove(projectile.group);
    this.flyingMeteors.splice(index, 1);
  }

  private clearFlyingMeteors(): void {
    for (const projectile of this.flyingMeteors) this.group.remove(projectile.group);
    this.flyingMeteors.length = 0;
  }

  private pushHistory(kind: SculptingUndoKind): void {
    this.history.push(this.captureHistoryState(kind));
    if (this.history.length > MAX_HISTORY) this.history.shift();
    this.redoHistory.length = 0;
  }

  private captureHistoryState(kind: SculptingUndoKind): SculptingHistoryState {
    return {
      kind,
      craters: this.craterRecords.map((crater) => ({
        id: crater.id,
        size: crater.size,
        normal: crater.normal.clone(),
        radius: crater.radius,
        depth: crater.depth,
        state: crater.state,
        lavaProgress: crater.lavaProgress,
        coolingProgress: crater.coolingProgress,
        fillScale: crater.fillScale,
      })),
      craterId: this.craterId,
      selectedCraterId: this.selectedCraterId,
      activeLavaIds: [...this.activeLavaIds],
      lastImpactNormal: this.lastImpactNormal?.clone() ?? null,
    };
  }

  private clearCraterRecords(): void {
    for (const crater of this.craterRecords) this.disposeCraterRecord(crater);
    this.craterRecords.length = 0;
  }

  private disposeCraterRecord(crater: CraterRecord): void {
    crater.basin.geometry.dispose();
    crater.lava.geometry.dispose();
    crater.mare.geometry.dispose();
    crater.selection.geometry.dispose();
    (crater.lava.material as THREE.Material).dispose();
    (crater.mare.material as THREE.Material).dispose();
    this.moonRoot.remove(crater.group);
  }

  private applyCraterVisualState(crater: CraterRecord): void {
    const lavaMaterial = crater.lava.material as THREE.MeshStandardMaterial;
    const mareMaterial = crater.mare.material as THREE.MeshStandardMaterial;
    const composite = this.getLavaCompositeProgress(crater);
    lavaMaterial.transparent = true;
    lavaMaterial.opacity = crater.state === 'cooling' ? 1 - composite : 1;
    lavaMaterial.color.copy(HOT_LAVA);
    lavaMaterial.emissive.copy(HOT_EMISSIVE);
    lavaMaterial.emissiveIntensity = 2.2;
    crater.basin.visible = crater.state !== 'mare' && (crater.state !== 'cooling' || composite <= 0.55);
    crater.lava.visible = crater.state === 'erupting' || (crater.state === 'cooling' && composite < 1);
    // Cooling uses this tiny surface patch as the animated composite. Baking
    // the whole 512x256 moon texture every frame caused severe tablet stalls.
    crater.mare.visible = crater.state === 'cooling' && composite < 1;
    mareMaterial.opacity = composite * MARE_TEXTURE_OPACITY;
    crater.selection.visible = false;

    if (crater.state === 'fresh') {
      crater.lava.scale.setScalar(0.04);
      crater.lava.position.z = -crater.depth * 0.82;
      crater.mare.scale.setScalar(0.04);
      crater.mare.position.z = -crater.depth * 0.82;
      return;
    }

    const filling = THREE.MathUtils.smoothstep(crater.lavaProgress, 0, LAVA_SPILL_START);
    const spilling = THREE.MathUtils.smoothstep(crater.lavaProgress, LAVA_SPILL_START, 1);
    const fillScale = crater.lavaProgress < LAVA_SPILL_START
      ? THREE.MathUtils.lerp(0.04, 0.58, filling)
      : THREE.MathUtils.lerp(0.58, 1, spilling);
    const lavaHeight = crater.lavaProgress < LAVA_SPILL_START
      ? THREE.MathUtils.lerp(-crater.depth * 0.82, LAVA_RIM_LEVEL, filling)
      : THREE.MathUtils.lerp(LAVA_RIM_LEVEL, LAVA_OVERFLOW_LEVEL, spilling);
    crater.fillScale = crater.state === 'erupting' ? fillScale : crater.fillScale;
    crater.lava.scale.setScalar(crater.fillScale);
    crater.lava.position.z = lavaHeight;
    crater.mare.scale.setScalar(crater.fillScale);
    crater.mare.position.z = lavaHeight;
    if (crater.state === 'cooling' || crater.state === 'mare') {
      crater.mareTextureMask = this.createMareTextureMask(crater);
    }

    if (crater.state === 'cooling') {
      lavaMaterial.color.lerpColors(HOT_LAVA, COOL_LAVA, composite);
      lavaMaterial.emissive.lerpColors(HOT_EMISSIVE, COOL_EMISSIVE, composite);
      lavaMaterial.emissiveIntensity = THREE.MathUtils.lerp(2.2, 0.04, composite);
    }
  }

  private createCrater(
    normal: THREE.Vector3,
    size: MeteorSize,
    forcedId: number | null = null,
    rebuild = true,
    forcedDepth: number | null = null,
  ): CraterRecord {
    let mareTextureChanged = false;
    if (this.craterRecords.length >= MAX_CRATERS) {
      const oldest = this.craterRecords.shift();
      if (oldest) {
        mareTextureChanged = oldest.state === 'mare';
        this.disposeCraterRecord(oldest);
      }
    }
    const id = forcedId ?? this.craterId++;
    if (forcedId !== null) this.craterId = Math.max(this.craterId, forcedId + 1);
    const tuning = SIZE_TUNING[size];
    const depth = forcedDepth ?? this.createCraterDepth(tuning.depth, id, size);
    const group = new THREE.Group();
    group.position.copy(normal).multiplyScalar(MOON_RADIUS);
    group.quaternion.setFromUnitVectors(FRONT, normal);
    const basin = new THREE.Mesh(
      createLunarCraterGeometry({ radius: tuning.crater, depth, moonRadius: MOON_RADIUS, seed: id }),
      this.craterMaterial,
    );
    basin.userData.craterId = id;
    const patchGeometry = createLunarSurfacePatchGeometry({
      radius: tuning.crater * 1.72,
      moonRadius: MOON_RADIUS,
      seed: id + 71,
    });
    const lava = new THREE.Mesh(patchGeometry, this.lavaMaterial.clone());
    lava.position.z = -depth * 0.82;
    lava.scale.setScalar(0.04);
    lava.visible = false;
    lava.userData.craterId = id;
    const mareMaterial = this.mareMaterial.clone();
    // The patch is alpha blended over the bright Moon, so its darker source
    // color matches the already-blended pixels after the final texture upload.
    mareMaterial.color.set(LUNAR_ASSET_COLORS.mare);
    mareMaterial.vertexColors = true;
    mareMaterial.transparent = true;
    mareMaterial.opacity = 0;
    mareMaterial.depthWrite = false;
    mareMaterial.polygonOffset = true;
    mareMaterial.polygonOffsetFactor = -1;
    const mare = new THREE.Mesh(
      createLunarSurfacePatchGeometry({
        radius: tuning.crater * 1.86,
        moonRadius: MOON_RADIUS,
        // Keep the temporary cooling patch coastline identical to the mask
        // that is baked into the Moon at the end of the transition.
        seed: id,
      }),
      mareMaterial,
    );
    mare.position.z = -depth * 0.82;
    mare.scale.setScalar(0.04);
    mare.visible = false;
    mare.userData.craterId = id;
    const selection = new THREE.LineSegments(this.createSelectionGeometry(tuning.crater * 1.62), this.selectionMaterial);
    selection.position.z = depth * 0.18 + 0.052;
    selection.visible = false;
    selection.userData.craterId = id;
    group.add(basin, mare, lava, selection);
    this.moonRoot.add(group);
    const record: CraterRecord = {
      id,
      size,
      normal: normal.clone(),
      radius: tuning.crater,
      depth,
      state: 'fresh',
      lavaProgress: 0,
      coolingProgress: 0,
      fillScale: 0.04,
      group,
      basin,
      lava,
      mare,
      mareTextureMask: null,
      selection,
    };
    this.craterRecords.push(record);
    if (rebuild) this.rebuildMoonSurface(mareTextureChanged);
    return record;
  }

  private createCraterDepth(baseDepth: number, id: number, size: MeteorSize): number {
    const sizeOffset = size === 'small' ? 0 : size === 'medium' ? 1 : 2;
    const variation = (id * 0.61803398875 + sizeOffset * 0.31 + 0.17) % 1;
    return baseDepth * THREE.MathUtils.lerp(CRATER_DEPTH_MIN_SCALE, CRATER_DEPTH_MAX_SCALE, variation);
  }

  private rebuildMoonSurface(updateTexture = true): void {
    const position = this.moonGeometry.getAttribute('position') as THREE.BufferAttribute;
    const color = this.moonGeometry.getAttribute('color') as THREE.BufferAttribute;
    for (let index = 0; index < position.count; index += 1) {
      const offset = index * 3;
      const baseX = this.moonBasePositions[offset];
      const baseY = this.moonBasePositions[offset + 1];
      const baseZ = this.moonBasePositions[offset + 2];
      const inverseLength = 1 / Math.hypot(baseX, baseY, baseZ);
      const nx = baseX * inverseLength;
      const ny = baseY * inverseLength;
      const nz = baseZ * inverseLength;
      let radialOffset = 0;
      let surfaceR = 1;
      let surfaceG = 1;
      let surfaceB = 1;

      for (const crater of this.craterRecords) {
        const mareBlend = crater.state === 'mare' ? 1 : this.getLavaCompositeProgress(crater);
        const dot = THREE.MathUtils.clamp(nx * crater.normal.x + ny * crater.normal.y + nz * crater.normal.z, -1, 1);
        const surfaceDistance = Math.sqrt(Math.max(0, 2 - 2 * dot)) * MOON_RADIUS;
        if (crater.state !== 'mare' && surfaceDistance < crater.radius * 1.48 && surfaceDistance >= crater.radius) {
          const ejectaT = (surfaceDistance / crater.radius - 1) / 0.48;
          const angle = Math.atan2(
            ny * crater.normal.z - nz * crater.normal.y,
            nx * crater.normal.y - ny * crater.normal.x + 0.0001,
          );
          const rayVariation = 0.5 + 0.5 * Math.sin(angle * (7 + (crater.id % 4)) + crater.id * 1.37);
          const coverage = (1 - THREE.MathUtils.smoothstep(ejectaT, 0, 1))
            * (0.16 + rayVariation * 0.12)
            * (1 - mareBlend);
          const ejectaShade = 0.9 + rayVariation * 0.055;
          surfaceR = THREE.MathUtils.lerp(surfaceR, ejectaShade, coverage);
          surfaceG = THREE.MathUtils.lerp(surfaceG, ejectaShade * 0.985, coverage);
          surfaceB = THREE.MathUtils.lerp(surfaceB, ejectaShade * 0.95, coverage);
        }
        if (surfaceDistance >= crater.radius) continue;
        const t = surfaceDistance / crater.radius;
        radialOffset += lunarCraterProfile(t, crater.depth);
        if (crater.state !== 'mare') {
          const craterShade = lunarCraterSurfaceShade(t);
          surfaceR = THREE.MathUtils.lerp(craterShade, 1, mareBlend);
          surfaceG = THREE.MathUtils.lerp(craterShade * 0.975, 1, mareBlend);
          surfaceB = THREE.MathUtils.lerp(craterShade * 0.93, 1, mareBlend);
        }
      }

      const deformedRadius = MOON_RADIUS + Math.max(radialOffset, -0.28);
      position.setXYZ(index, nx * deformedRadius, ny * deformedRadius, nz * deformedRadius);
      color.setXYZ(index, surfaceR, surfaceG, surfaceB);
    }
    position.needsUpdate = true;
    color.needsUpdate = true;
    this.moonGeometry.computeVertexNormals();
    this.moonGeometry.computeBoundingSphere();
    if (updateTexture) this.rebuildMoonTexture();
  }

  private rebuildMoonTexture(): void {
    if (!this.moonTextureCanvas || !this.moonBasePixels || !this.moonTextureImageData) return;
    const context = this.moonTextureCanvas.getContext('2d');
    if (!context) return;
    this.moonTextureImageData.data.set(this.moonBasePixels);
    for (const crater of this.craterRecords) {
      if (crater.state !== 'mare') continue;
      this.applyMareTextureMask(crater);
    }
    context.putImageData(this.moonTextureImageData, 0, 0);
    this.moonTexture.needsUpdate = true;
  }

  private createMareTextureMask(crater: CraterRecord): MareTextureMask {
    if (!this.moonTextureCanvas || !this.moonTextureDirections) {
      return { offsets: new Uint32Array(), colors: new Uint8ClampedArray(), opacities: new Uint8Array() };
    }
    const offsets: number[] = [];
    const colors: number[] = [];
    const opacities: number[] = [];
    const directions = this.moonTextureDirections;
    const reference = Math.abs(crater.normal.y) < 0.9
      ? new THREE.Vector3(0, 1, 0)
      : new THREE.Vector3(1, 0, 0);
    const tangentX = reference.cross(crater.normal).normalize();
    const tangentY = new THREE.Vector3().crossVectors(crater.normal, tangentX).normalize();
    const pixelCount = this.moonTextureCanvas.width * this.moonTextureCanvas.height;

    for (let pixel = 0; pixel < pixelCount; pixel += 1) {
      const directionOffset = pixel * 3;
      const nx = directions[directionOffset];
      const ny = directions[directionOffset + 1];
      const nz = directions[directionOffset + 2];
      const dot = THREE.MathUtils.clamp(
        nx * crater.normal.x + ny * crater.normal.y + nz * crater.normal.z,
        -1,
        1,
      );
      const surfaceDistance = Math.sqrt(Math.max(0, 2 - 2 * dot)) * MOON_RADIUS;
      if (surfaceDistance >= crater.radius * 2.15 * crater.fillScale) continue;
      const localX = nx * tangentX.x + ny * tangentX.y + nz * tangentX.z;
      const localY = nx * tangentY.x + ny * tangentY.y + nz * tangentY.z;
      const angle = Math.atan2(localY, localX);
      const coastVariation =
        Math.sin(angle * 3 + crater.id * 0.83) * 0.085
        + Math.sin(angle * 5 - crater.id * 0.47) * 0.045
        + Math.sin(angle * 9 + crater.id * 1.19) * 0.022;
      const mareRadius = crater.radius * 1.86 * crater.fillScale * (1 + coastVariation);
      if (surfaceDistance >= mareRadius) continue;
      const coverage = 1 - THREE.MathUtils.smoothstep(surfaceDistance / mareRadius, 0.7, 1);
      const mottling =
        Math.sin(nx * 43 + ny * 61 + nz * 37 + crater.id) * 3.5
        + Math.sin(nx * 97 - ny * 53 + nz * 71 - crater.id * 0.7) * 2;
      offsets.push(pixel * 4);
      colors.push(
        Math.round(94 + mottling),
        Math.round(95 + mottling * 0.92),
        Math.round(92 + mottling * 0.78),
      );
      opacities.push(Math.round(coverage * MARE_TEXTURE_OPACITY * 255));
    }
    return {
      offsets: Uint32Array.from(offsets),
      colors: Uint8ClampedArray.from(colors),
      opacities: Uint8Array.from(opacities),
    };
  }

  private applyMareTextureMask(crater: CraterRecord): void {
    if (!this.moonTextureImageData) return;
    crater.mareTextureMask ??= this.createMareTextureMask(crater);
    const { offsets, colors, opacities } = crater.mareTextureMask;
    const pixels = this.moonTextureImageData.data;
    for (let index = 0; index < offsets.length; index += 1) {
      const offset = offsets[index];
      const colorOffset = index * 3;
      const opacity = opacities[index] / 255;
      pixels[offset] = Math.round(THREE.MathUtils.lerp(pixels[offset], colors[colorOffset], opacity));
      pixels[offset + 1] = Math.round(THREE.MathUtils.lerp(pixels[offset + 1], colors[colorOffset + 1], opacity));
      pixels[offset + 2] = Math.round(THREE.MathUtils.lerp(pixels[offset + 2], colors[colorOffset + 2], opacity));
    }
  }

  private commitMoonTexture(): void {
    if (!this.moonTextureCanvas || !this.moonTextureImageData) return;
    const context = this.moonTextureCanvas.getContext('2d');
    if (!context) return;
    context.putImageData(this.moonTextureImageData, 0, 0);
    this.moonTexture.needsUpdate = true;
  }

  private createSelectionGeometry(radius: number): THREE.BufferGeometry {
    const positions: number[] = [];
    for (let marker = 0; marker < 4; marker += 1) {
      const angle = marker * Math.PI * 0.5;
      const halfArc = 0.12;
      positions.push(
        Math.cos(angle - halfArc) * radius,
        Math.sin(angle - halfArc) * radius,
        0,
        Math.cos(angle + halfArc) * radius,
        Math.sin(angle + halfArc) * radius,
        0,
      );
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    return geometry;
  }

  private updateLava(delta: number): void {
    if (this.activeLavaIds.size === 0) return;
    let surfaceChanged = false;
    const completedMares: CraterRecord[] = [];

    for (const id of [...this.activeLavaIds]) {
      const crater = this.craterRecords.find((candidate) => candidate.id === id);
      if (!crater) {
        this.activeLavaIds.delete(id);
        continue;
      }

      if (crater.state === 'erupting') {
        crater.lavaProgress = Math.min(1, crater.lavaProgress + delta / LAVA_DURATION);
        const filling = THREE.MathUtils.smoothstep(crater.lavaProgress, 0, LAVA_SPILL_START);
        const spilling = THREE.MathUtils.smoothstep(crater.lavaProgress, LAVA_SPILL_START, 1);
        crater.fillScale = crater.lavaProgress < LAVA_SPILL_START
          ? THREE.MathUtils.lerp(0.04, 0.58, filling)
          : THREE.MathUtils.lerp(0.58, 1, spilling);
        crater.lava.scale.setScalar(crater.fillScale);
        crater.lava.position.z = crater.lavaProgress < LAVA_SPILL_START
          ? THREE.MathUtils.lerp(-crater.depth * 0.82, LAVA_RIM_LEVEL, filling)
          : THREE.MathUtils.lerp(LAVA_RIM_LEVEL, LAVA_OVERFLOW_LEVEL, spilling);
        if (crater.lavaProgress >= 1) this.beginCooling(crater);
        continue;
      }

      if (crater.state !== 'cooling') {
        this.activeLavaIds.delete(id);
        continue;
      }

      crater.coolingProgress = Math.min(1, crater.coolingProgress + delta / COOLING_DURATION);
      const cooling = this.getLavaCompositeProgress(crater);
      const material = crater.lava.material as THREE.MeshStandardMaterial;
      const mareMaterial = crater.mare.material as THREE.MeshStandardMaterial;
      material.color.lerpColors(HOT_LAVA, COOL_LAVA, cooling);
      material.emissive.lerpColors(HOT_EMISSIVE, COOL_EMISSIVE, cooling);
      material.emissiveIntensity = THREE.MathUtils.lerp(2.2, 0.04, cooling);
      material.opacity = 1 - cooling;
      crater.mare.visible = cooling < 1;
      mareMaterial.opacity = cooling * MARE_TEXTURE_OPACITY;
      crater.basin.visible = cooling <= 0.55;
      if (crater.coolingProgress >= 1) {
        crater.state = 'mare';
        crater.lava.visible = false;
        crater.basin.visible = false;
        crater.mare.visible = false;
        material.dispose();
        this.activeLavaIds.delete(id);
        surfaceChanged = true;
        completedMares.push(crater);
      }
    }

    if (surfaceChanged) this.rebuildMoonSurface(false);
    if (completedMares.length > 0) {
      for (const crater of completedMares) this.applyMareTextureMask(crater);
      this.commitMoonTexture();
      if (completedMares.some((crater) => crater.id === this.selectedCraterId)) this.selectCrater(null);
    }
  }

  private beginCooling(crater: CraterRecord): void {
    crater.state = 'cooling';
    crater.coolingProgress = 0;
    crater.fillScale = crater.lava.scale.x;
    const material = crater.lava.material as THREE.MeshStandardMaterial;
    material.transparent = true;
    material.opacity = 1;
    const mareMaterial = crater.mare.material as THREE.MeshStandardMaterial;
    mareMaterial.opacity = 0;
    crater.mare.visible = true;
    crater.mare.scale.setScalar(crater.fillScale);
    crater.mare.position.z = crater.lava.position.z;
    crater.mareTextureMask = this.createMareTextureMask(crater);
    this.activeLavaIds.add(crater.id);
  }

  private getLavaCompositeProgress(crater: CraterRecord): number {
    if (crater.state === 'mare') return 1;
    if (crater.state !== 'cooling') return 0;
    return THREE.MathUtils.smootherstep(crater.coolingProgress, 0, 1);
  }

  private updateLavaLightPosition(crater: CraterRecord): void {
    this.lavaLight.position.copy(crater.normal).multiplyScalar(MOON_RADIUS + 1.1).applyQuaternion(this.moonRoot.quaternion);
    this.lavaLight.position.add(this.moonRoot.position);
  }

  private updateBursts(delta: number, reducedMotion: boolean): void {
    for (let index = this.bursts.length - 1; index >= 0; index -= 1) {
      const burst = this.bursts[index];
      if (!burst.update(delta, reducedMotion)) continue;
      this.group.remove(burst.group);
      burst.dispose();
      this.bursts.splice(index, 1);
    }
  }

  private clearBursts(): void {
    for (const burst of this.bursts) {
      this.group.remove(burst.group);
      burst.dispose();
    }
    this.bursts.length = 0;
  }

  private selectCraterAt(event: PointerEvent): void {
    this.updateRay(event);
    const candidates = this.craterRecords
      .filter((crater) => crater.state !== 'mare')
      .flatMap((crater) => [crater.basin, crater.lava, crater.mare]);
    const hit = this.raycaster.intersectObjects(candidates, false)[0];
    const id = typeof hit?.object.userData.craterId === 'number' ? hit.object.userData.craterId : null;
    this.selectCrater(id);
  }

  private selectCrater(id: number | null): void {
    this.selectedCraterId = id;
    for (const crater of this.craterRecords) crater.selection.visible = crater.id === id;
    const selected = id === null ? null : this.craterRecords.find((crater) => crater.id === id) ?? null;
    if (selected && this.activeLavaIds.has(selected.id)) this.updateLavaLightPosition(selected);
  }

  private updateTrajectory(): void {
    const attribute = this.trajectoryGeometry.getAttribute('position') as THREE.BufferAttribute;
    const point = this.meteor.position.clone();
    const velocity = this.projectileVelocity.clone();
    this.getMoonWorldCenter(this.moonWorldCenter);
    const step = 0.075;
    const meteorRadius = 0.19 * SIZE_TUNING[this.meteorSize].meteor;
    let visiblePoints = attribute.count;
    let predictedHit: THREE.Vector3 | null = null;
    for (let index = 0; index < attribute.count; index += 1) {
      attribute.setXYZ(index, point.x, point.y, point.z);
      if (index === attribute.count - 1) break;
      const gravity = this.moonWorldCenter.clone().sub(point);
      const distanceSq = Math.max(gravity.lengthSq(), 2.5);
      gravity.normalize().multiplyScalar(MOON_GRAVITY * (9 / distanceSq));
      velocity.addScaledVector(gravity, step);
      const nextPoint = point.clone().addScaledVector(velocity, step);
      const hit = this.segmentSphereHit(point, nextPoint, this.moonWorldCenter, MOON_RADIUS + meteorRadius);
      if (hit) {
        attribute.setXYZ(index + 1, hit.x, hit.y, hit.z);
        visiblePoints = index + 2;
        predictedHit = hit;
        break;
      }
      point.copy(nextPoint);
    }
    this.trajectoryGeometry.setDrawRange(0, visiblePoints);
    attribute.needsUpdate = true;
    this.trajectoryGeometry.computeBoundingSphere();
    this.trajectory.computeLineDistances();
    this.trajectory.visible = true;
    this.updateImpactMarker(predictedHit);
  }

  private updateImpactMarker(hit: THREE.Vector3 | null): void {
    if (!hit) {
      this.predictedImpactNormal = null;
      this.impactMarker.visible = false;
      this.trajectoryMaterial.color.set('#ffb184');
      this.trajectoryMaterial.opacity = 0.68;
      return;
    }

    const normal = hit.clone().sub(this.moonWorldCenter).normalize();
    this.predictedImpactNormal = normal.clone().applyQuaternion(this.moonRoot.quaternion.clone().invert());
    this.impactMarker.position.copy(this.moonWorldCenter).addScaledVector(normal, MOON_RADIUS + 0.035);
    this.impactMarker.quaternion.setFromUnitVectors(FRONT, normal);
    this.impactMarkerBaseScale = SIZE_TUNING[this.meteorSize].crater;
    this.impactMarker.scale.setScalar(this.impactMarkerBaseScale);
    this.impactMarker.visible = true;
    this.trajectoryMaterial.color.set('#fff09a');
    this.trajectoryMaterial.opacity = 1;
  }

  private updateSlingBand(): void {
    const visible = this.state === 'ready' || this.state === 'aiming';
    this.slingBandLeft.visible = visible;
    this.slingBandRight.visible = visible;
    this.slingPouch.visible = visible;
    if (!visible) return;

    const center = (this.state === 'aiming' ? this.meteor.position : this.launcherAnchor)
      .clone()
      .sub(this.sling.position);
    this.slingPouch.position.copy(center).add(new THREE.Vector3(0, -0.01, -0.045));
    const leftPouchEdge = center.clone().add(new THREE.Vector3(-0.105, 0, 0));
    const rightPouchEdge = center.clone().add(new THREE.Vector3(0.105, 0, 0));
    this.placeSlingBand(this.slingBandLeft, this.slingLeftAnchor, leftPouchEdge);
    this.placeSlingBand(this.slingBandRight, this.slingRightAnchor, rightPouchEdge);
  }

  private placeSlingBand(mesh: THREE.Mesh, start: THREE.Vector3, end: THREE.Vector3): void {
    const direction = end.clone().sub(start);
    const length = Math.max(direction.length(), 0.001);
    mesh.position.copy(start).add(end).multiplyScalar(0.5);
    mesh.scale.set(1, length, 1);
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  }

  private updateRay(event: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    this.pointerNdc.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.camera.updateMatrixWorld();
    this.group.updateWorldMatrix(true, true);
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
  }

  private pointerToPlane(event: PointerEvent, target: THREE.Vector3): boolean {
    this.updateRay(event);
    return this.raycaster.ray.intersectPlane(this.interactionPlane, target) !== null;
  }

  private getMoonWorldCenter(target: THREE.Vector3): THREE.Vector3 {
    return this.moonRoot.getWorldPosition(target);
  }

  private cancelPointer(): void {
    if (this.activePointerId !== null && this.canvas.hasPointerCapture?.(this.activePointerId)) {
      this.canvas.releasePointerCapture(this.activePointerId);
    }
    this.activePointerId = null;
    this.pointerMode = 'none';
    this.pointerMoved = false;
  }

  private segmentSphereHit(
    start: THREE.Vector3,
    end: THREE.Vector3,
    center: THREE.Vector3,
    radius: number,
  ): THREE.Vector3 | null {
    const segment = end.clone().sub(start);
    const lengthSq = segment.lengthSq();
    if (lengthSq <= 1e-8) return null;
    const t = THREE.MathUtils.clamp(center.clone().sub(start).dot(segment) / lengthSq, 0, 1);
    const closest = start.clone().addScaledVector(segment, t);
    return closest.distanceToSquared(center) <= radius * radius ? closest : null;
  }
}
